import React from "react";
import { BannerEditor, BannerEditorPictures } from "../BannerEditor";
import { ManagerProfileSection } from "../ManagerProfileSection";
import { useManagerProfile } from "../ManagerProfileProvider";
import { HStack } from "@chakra-ui/react";
import { Button } from "../../common";
import { useStandardApiHandler } from "../../../error-handling/useStandardApiErrorHandler";
import { useBannerImageModel } from "../../../api/hooks/models/useBannerImageModel";
import { useBooleanState, useVeriToast } from "../../../hooks";
import {
  bannerLayoutExists,
  mapBannerImagesToEditorImages,
} from "../BannerEditor/helpers";
import { ConfirmModal } from "../../modals";

export const ManagerBannerEditor: React.FC<{
  refreshManager?: () => void;
}> = ({ refreshManager }) => {
  const { setEditingDrawer, manager } = useManagerProfile(true);

  const bannerImageModel = useBannerImageModel();
  const { toastSuccess } = useVeriToast();

  const { wrapApiRequest, isLoading: isPictureUpdaterLoading } =
    useStandardApiHandler(false);

  const layoutImages = manager?.bannerLayout
    ? Number(manager.bannerLayout.replace("img", "") || 0)
    : 0;

  const [bannerImagesLayout, setBannerImagesLayout] =
    React.useState(layoutImages);
  const [bannerImages, setBannerImages] = React.useState<BannerEditorPictures>(
    mapBannerImagesToEditorImages(manager.bannerImages || [], layoutImages),
  );

  const handleBannerUpdate = wrapApiRequest(async () => {
    if (!bannerLayoutExists(manager.bannerImages || [], bannerImagesLayout)) {
      await bannerImageModel.createManagerLayout(
        String(manager.id),
        bannerImagesLayout,
        bannerImages,
      );
    } else {
      await bannerImageModel.updateManagerLayout(
        String(manager.id),
        bannerImagesLayout,
        bannerImages,
      );
    }

    toastSuccess("Banner updated successfully");
    setEditingDrawer(null);
    refreshManager?.();
  });

  const isChangeLayoutConfirmModalOpen = useBooleanState(false);

  const handleSetBannerImagesLayout = (layout: number) => {
    setBannerImagesLayout(layout);
    setBannerImages(
      mapBannerImagesToEditorImages(manager.bannerImages || [], layout),
    );
  };

  return (
    <ManagerProfileSection
      actionLabel={"Cancel"}
      action={setEditingDrawer.bind(null, null)}
      title={"Banner"}
      extraActions={
        <HStack spacing={2}>
          {bannerImagesLayout > 0 && (
            <>
              <Button
                isDisabled={isPictureUpdaterLoading}
                onClick={isChangeLayoutConfirmModalOpen.true}
                outline
              >
                Change layout
              </Button>
              <Button
                isLoading={isPictureUpdaterLoading}
                onClick={handleBannerUpdate}
              >
                Save changes
              </Button>
            </>
          )}
        </HStack>
      }
    >
      <BannerEditor
        onChange={setBannerImages}
        onLayoutChange={handleSetBannerImagesLayout}
        images={bannerImages}
        layout={bannerImagesLayout}
      />
      <ConfirmModal
        isOpen={isChangeLayoutConfirmModalOpen.state}
        onClose={isChangeLayoutConfirmModalOpen.false}
        message={"Are you sure you want to change your banner layout?"}
        title={"Change banner layout"}
        confirmLabel={"Yes, continue"}
        cancelLabel={"No"}
        onConfirm={() => {
          handleSetBannerImagesLayout(0);
          isChangeLayoutConfirmModalOpen.false();
        }}
      />
    </ManagerProfileSection>
  );
};
